// web/src/billing/usePostSignupRedirect.ts
//
// After /auth/confirm succeeds, check whether the user came from
// /pricing with a plan preselected (via /auth/signup?plan=...&cadence=...).
// If so, kick off /billing/checkout-session and redirect to Stripe.
//
// The plan is stashed in localStorage by SignupForm (read from URL
// params); a 24h freshness window protects against zombie state.
import { useEffect, useRef } from "react";
import { useCheckoutSession } from "./api";


export const PENDING_CHECKOUT_KEY = "tapelens.pending_checkout";
const MAX_AGE_MS = 24 * 60 * 60 * 1000;


interface PendingCheckout {
  plan: "pro" | "analyst";
  cadence: "monthly" | "annual";
  savedAt: number;
}


export function stashPendingCheckout(args: { plan: "pro" | "analyst"; cadence: "monthly" | "annual" }) {
  try {
    const payload: PendingCheckout = { ...args, savedAt: Date.now() };
    window.localStorage.setItem(PENDING_CHECKOUT_KEY, JSON.stringify(payload));
  } catch {}
}


function readPending(): PendingCheckout | null {
  try {
    const raw = window.localStorage.getItem(PENDING_CHECKOUT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingCheckout;
    if (typeof parsed.savedAt !== "number") return null;
    if (Date.now() - parsed.savedAt > MAX_AGE_MS) {
      window.localStorage.removeItem(PENDING_CHECKOUT_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}


/**
 * Mount once on the page that the email-confirm flow lands on.
 * Pass `enabled=true` only when the auth state confirms the user is
 * signed in — otherwise the API call will 401.
 */
export function usePostSignupRedirect(enabled: boolean): void {
  const checkout = useCheckoutSession();
  const fired = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    if (fired.current) return;
    const pending = readPending();
    if (!pending) return;
    fired.current = true;
    window.localStorage.removeItem(PENDING_CHECKOUT_KEY);
    checkout
      .mutateAsync({ plan: pending.plan, cadence: pending.cadence })
      .then((res) => {
        window.location.href = res.url;
      })
      .catch(() => {
        // Swallow — user can still upgrade from /pricing later.
      });
  }, [enabled, checkout]);
}
